import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import ServiceCostPresentation from "./ServiceCostPresentation";
import { formatServiceCost, serviceCostSummary } from "../../lib/serviceCost";

const inputClass = "wms-input h-10 w-full text-sm";

export function ServiceCostDiscountControl({
  checked,
  percentage,
  reason,
  editable,
  onEnabledChange,
  onPercentageChange,
  onReasonChange,
  testid = "service-cost-discount",
  readableViewValues = false,
}) {
  const valueClass = readableViewValues ? " discount-view-value" : "";
  return <div className="grid gap-3 rounded-sm border p-4 md:grid-cols-[auto_150px_1fr] md:items-end" data-testid={testid}>
    <label className="flex h-10 items-center gap-2 text-sm font-medium">
      <input type="checkbox" checked={checked} disabled={!editable} onChange={(event) => onEnabledChange(event.target.checked)} />
      Apply Discount
    </label>
    <label className="text-xs font-medium">Percentage (%)
      <input className={`${inputClass} mt-1${valueClass}`} aria-label="Discount percentage" disabled={!editable} value={percentage} onChange={(event) => onPercentageChange(event.target.value)} />
    </label>
    <label className="text-xs font-medium">Reason
      <input className={`${inputClass} mt-1${valueClass}`} aria-label="Discount reason" disabled={!editable} value={reason} onChange={(event) => onReasonChange(event.target.value)} />
    </label>
  </div>;
}

/** The Move surface deliberately exposes only Custom Costs and Discount. */
export function CompactServiceCostSection({ value, draft, onChange, editable, error = "" }) {
  const updateRow = (key, field, next) => onChange({
    ...draft,
    custom_costs: draft.custom_costs.map((row) => row._key === key ? { ...row, [field]: next } : row),
  });
  const removeRow = (key) => onChange({ ...draft, custom_costs: draft.custom_costs.filter((row) => row._key !== key) });
  return <section className="space-y-3 rounded-sm border p-4" data-testid="service-move-cost-section">
    <h3 className="text-sm font-semibold">Custom Costs</h3>
    <div className="space-y-3">
      {draft.custom_costs.map((row) => <div key={row._key} className="grid gap-2 md:grid-cols-[1fr_90px_140px_auto]">
        <input className={inputClass} aria-label="Custom Cost description" disabled={!editable} value={row.description} onChange={(event) => updateRow(row._key, "description", event.target.value)} placeholder="Description" />
        <input className={inputClass} aria-label="Custom Cost quantity" disabled={!editable} type="number" min="1" value={row.quantity} onChange={(event) => updateRow(row._key, "quantity", event.target.value)} />
        <input className={inputClass} aria-label="Custom Cost unit price" disabled={!editable} inputMode="decimal" value={row.unit_price} onChange={(event) => updateRow(row._key, "unit_price", event.target.value)} placeholder="Unit Price" />
        <Button type="button" variant="transfer-remove" size="icon" disabled={!editable} aria-label="Remove Custom Cost" onClick={() => removeRow(row._key)}><Trash2 /></Button>
      </div>)}
    </div>
    <Button type="button" size="sm" variant="transfer-add" disabled={!editable} onClick={() => onChange({ ...draft, custom_costs: [...draft.custom_costs, { _key: `new-${Date.now()}`, description: "", quantity: "1", unit_price: "0" }] })}><Plus />Add Cost</Button>
    <ServiceCostDiscountControl
      checked={draft.discount.enabled}
      percentage={draft.discount.percentage}
      reason={draft.discount.reason}
      editable={editable}
      testid="service-move-cost-discount"
      onEnabledChange={(enabled) => onChange({ ...draft, discount: { ...draft.discount, enabled } })}
      onPercentageChange={(percentage) => onChange({ ...draft, discount: { ...draft.discount, percentage } })}
      onReasonChange={(reason) => onChange({ ...draft, discount: { ...draft.discount, reason } })}
    />
    {error ? <p className="text-xs text-destructive">{error}</p> : null}
  </section>;
}

export function ServiceCostLinearSummary({
  gross,
  discountAmount,
  final,
  labels = ["Gross Service Cost", "Discount Amount", "Final Service Cost"],
  testid = "service-cost-summary",
}) {
  return <dl className="grid gap-3 border-t pl-8 pt-4 sm:grid-cols-3" data-testid={testid} data-summary-layout="linear" data-summary-container="ticket-footer">
    <div className="min-w-0"><dt className="text-xs text-muted-foreground">{labels[0]}</dt><dd className="mt-1 break-words text-base font-semibold tabular-nums">{formatServiceCost(gross)}</dd></div>
    <div className="min-w-0"><dt className="text-xs text-muted-foreground">{labels[1]}</dt><dd className="mt-1 break-words text-base font-semibold tabular-nums text-destructive">- {formatServiceCost(discountAmount)}</dd></div>
    <div className="min-w-0"><dt className="text-xs font-medium">{labels[2]}</dt><dd className="mt-1 break-words text-lg font-bold tabular-nums text-primary">{formatServiceCost(final)}</dd></div>
  </dl>;
}

export function UnifiedServiceCostSection({ value, draft, preview, onChange, editable = false, error = "", summaryLayout = "stacked", readableViewValues = false }) {
  const displayedCost = editable ? preview : value;
  const summary = serviceCostSummary(displayedCost);
  const customRows = editable ? draft.custom_costs : value?.custom_costs || [];
  const updateRow = (key, field, next) => onChange({
    ...draft,
    custom_costs: draft.custom_costs.map((row) => row._key === key ? { ...row, [field]: next } : row),
  });
  const removeRow = (key) => onChange({
    ...draft,
    custom_costs: draft.custom_costs.filter((row) => row._key !== key),
  });

  return (
    <div className="space-y-5" data-testid="service-cost-section">
      <div className="overflow-x-auto rounded-sm border" data-testid="service-cost-ledger">
        <table className="w-full min-w-[760px] table-fixed text-left text-sm">
          <colgroup>
            <col className="w-[120px]" />
            <col />
            <col className="w-16" />
            <col className="w-36" />
            <col className="w-36" />
            <col className="w-20" />
          </colgroup>
          <thead className="bg-muted/40 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2" data-ledger-column="description">Description</th>
              <th className="px-2 py-2 text-right" data-ledger-column="qty">Qty</th>
              <th className="px-3 py-2 text-right">Unit Price</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {(value?.spare_part_costs || []).map((row) => (
              <tr key={row.usage_id} data-cost-type="spare-part" className="border-t">
                <td className="px-3 py-3 font-medium">Spare Part</td>
                <td className="break-words px-3 py-3">
                  {row.part_name || row.part_number || "Spare Part"}
                  {row.part_number ? <span className="ml-2 text-xs text-muted-foreground">{row.part_number}</span> : null}
                </td>
                <td className="px-2 py-3 text-right tabular-nums">{row.quantity}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatServiceCost(row.unit_price_snapshot)}</td>
                <td className="px-3 py-3 text-right font-medium tabular-nums">{formatServiceCost(row.line_total)}</td>
                <td className="px-3 py-3 text-right text-muted-foreground">-</td>
              </tr>
            ))}
            {customRows.map((row) => editable ? (
              <tr key={row._key} data-cost-type="custom" className="border-t">
                <td className="px-3 py-3 font-medium">Custom Cost</td>
                <td className="px-3 py-3"><input className={inputClass} aria-label="Custom Cost description" value={row.description} onChange={(event) => updateRow(row._key, "description", event.target.value)} placeholder="Description" /></td>
                <td className="px-2 py-3"><input className={`${inputClass} px-2 text-right`} aria-label="Custom Cost quantity" type="number" min="1" value={row.quantity} onChange={(event) => updateRow(row._key, "quantity", event.target.value)} /></td>
                <td className="px-3 py-3"><input className={`${inputClass} text-right`} aria-label="Custom Cost unit price" inputMode="decimal" value={row.unit_price} onChange={(event) => updateRow(row._key, "unit_price", event.target.value)} /></td>
                <td className="px-3 py-3 text-right font-medium tabular-nums">
                  {preview?.customLineTotals?.[row._key] === null ? "-" : formatServiceCost(preview?.customLineTotals?.[row._key] || "0")}
                </td>
                <td className="px-3 py-3 text-right"><Button type="button" variant="transfer-remove" size="icon" className="h-10 w-10" aria-label="Remove Custom Cost" onClick={() => removeRow(row._key)}><Trash2 /></Button></td>
              </tr>
            ) : (
              <tr key={row.id} data-cost-type="custom" className="border-t">
                <td className="px-3 py-3 font-medium">Custom Cost</td>
                <td className="break-words px-3 py-3">{row.description}</td>
                <td className="px-2 py-3 text-right tabular-nums">{row.quantity}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatServiceCost(row.unit_price)}</td>
                <td className="px-3 py-3 text-right font-medium tabular-nums">{formatServiceCost(row.line_total)}</td>
                <td className="px-3 py-3 text-right text-muted-foreground">-</td>
              </tr>
            ))}
            {!(value?.spare_part_costs || []).length && !customRows.length ? (
              <tr><td colSpan="6" className="px-3 py-8 text-center text-muted-foreground">No Service Cost rows.</td></tr>
            ) : null}
          </tbody>
        </table>
        {editable ? (
          <div className="flex justify-end border-t px-3 py-3">
            <Button type="button" size="sm" variant="transfer-add" onClick={() => onChange({
              ...draft,
              custom_costs: [...draft.custom_costs, { _key: `new-${Date.now()}`, description: "", quantity: "1", unit_price: "0" }],
            })}><Plus />Add Cost</Button>
          </div>
        ) : null}
      </div>

      <ServiceCostDiscountControl
        checked={editable ? draft.discount.enabled : value?.discount?.enabled === true}
        percentage={editable ? draft.discount.percentage : value?.discount?.percentage || "0"}
        reason={editable ? draft.discount.reason : value?.discount?.reason || ""}
        editable={editable}
        readableViewValues={readableViewValues}
        onEnabledChange={(enabled) => onChange({ ...draft, discount: { ...draft.discount, enabled } })}
        onPercentageChange={(percentage) => onChange({ ...draft, discount: { ...draft.discount, percentage } })}
        onReasonChange={(reason) => onChange({ ...draft, discount: { ...draft.discount, reason } })}
      />
      {summaryLayout === "linear" ? (
        <ServiceCostLinearSummary gross={summary.gross} discountAmount={summary.discountAmount} final={summary.final} />
      ) : (
        <dl className="ml-auto grid max-w-lg grid-cols-[1fr_auto] gap-x-8 gap-y-2 rounded-sm border p-4 text-sm" data-testid="service-cost-summary">
          <dt className="text-muted-foreground">Gross Service Cost</dt><dd className="text-right tabular-nums">{formatServiceCost(summary.gross)}</dd>
          <dt className="text-muted-foreground">Discount Amount</dt><dd className="text-right tabular-nums">{formatServiceCost(summary.discountAmount)}</dd>
          <dt className="font-semibold">Final Service Cost</dt><dd className="text-right font-semibold tabular-nums text-primary">{formatServiceCost(summary.final)}</dd>
        </dl>
      )}
      {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
    </div>
  );
}

function LegacyServiceCostSection({ value, draft, onChange, editable = false, error = "" }) {
  const summary = serviceCostSummary(value);
  const updateRow = (key, field, next) => onChange({
    ...draft,
    custom_costs: draft.custom_costs.map((row) => row._key === key ? { ...row, [field]: next } : row),
  });
  const removeRow = (key) => onChange({
    ...draft,
    custom_costs: draft.custom_costs.filter((row) => row._key !== key),
  });
  return (
    <div className="space-y-5" data-testid="service-cost-section">
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-sm border bg-muted/30 p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Service Cost</div>
          <div className="mt-2"><ServiceCostPresentation serviceCost={value} /></div>
        </div>
        <div className="rounded-sm border bg-muted/20 p-4 lg:col-span-2">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Spare Part Cost · Read only</div>
          {(value?.spare_part_costs || []).length ? (
            <div className="space-y-2">
              {value.spare_part_costs.map((row) => (
                <div key={row.usage_id} className="grid gap-2 text-sm sm:grid-cols-[1fr_auto_auto]">
                  <span>{row.part_name || row.part_number || "Spare Part"}<span className="ml-2 text-xs text-muted-foreground">{row.part_number}</span></span>
                  <span className="text-muted-foreground">{row.quantity} × {formatServiceCost(row.unit_price_snapshot)}</span>
                  <span className="font-medium tabular-nums">{formatServiceCost(row.line_total)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No Spare Part Cost.</p>}
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold">Custom Cost</h4>
            <p className="text-xs text-muted-foreground">Additional PPN-inclusive service charges.</p>
          </div>
          {editable ? <Button type="button" size="sm" variant="transfer-add" onClick={() => onChange({
            ...draft,
            custom_costs: [...draft.custom_costs, { _key: `new-${Date.now()}`, description: "", quantity: "1", unit_price: "0" }],
          })}><Plus />Add Cost</Button> : null}
        </div>
        <div className="space-y-2">
          {(editable ? draft.custom_costs : value?.custom_costs || []).map((row) => editable ? (
            <div key={row._key} className="grid gap-2 rounded-sm border p-3 sm:grid-cols-[minmax(180px,1fr)_100px_160px_40px]">
              <input className={inputClass} aria-label="Custom Cost description" value={row.description} onChange={(event) => updateRow(row._key, "description", event.target.value)} placeholder="Description" />
              <input className={inputClass} aria-label="Custom Cost quantity" type="number" min="1" value={row.quantity} onChange={(event) => updateRow(row._key, "quantity", event.target.value)} />
              <input className={inputClass} aria-label="Custom Cost unit price" inputMode="decimal" value={row.unit_price} onChange={(event) => updateRow(row._key, "unit_price", event.target.value)} />
              <Button type="button" variant="transfer-remove" size="icon" className="h-10 w-10" aria-label="Remove Custom Cost" onClick={() => removeRow(row._key)}><Trash2 /></Button>
            </div>
          ) : (
            <div key={row.id} className="grid gap-2 text-sm sm:grid-cols-[1fr_auto_auto]">
              <span>{row.description}</span><span className="text-muted-foreground">{row.quantity} × {formatServiceCost(row.unit_price)}</span><span className="font-medium tabular-nums">{formatServiceCost(row.line_total)}</span>
            </div>
          ))}
          {!editable && !(value?.custom_costs || []).length ? <p className="text-sm text-muted-foreground">No Custom Cost.</p> : null}
        </div>
      </div>

      <div className="grid gap-3 rounded-sm border p-4 md:grid-cols-[auto_150px_1fr] md:items-end">
        <label className="flex h-10 items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={editable ? draft.discount.enabled : value?.discount?.enabled === true} disabled={!editable} onChange={(event) => onChange({ ...draft, discount: { ...draft.discount, enabled: event.target.checked } })} />
          Apply Discount
        </label>
        <label className="text-xs font-medium">Percentage
          <input className={`${inputClass} mt-1`} aria-label="Discount percentage" disabled={!editable} value={editable ? draft.discount.percentage : value?.discount?.percentage || "0"} onChange={(event) => onChange({ ...draft, discount: { ...draft.discount, percentage: event.target.value } })} />
        </label>
        <label className="text-xs font-medium">Reason
          <input className={`${inputClass} mt-1`} aria-label="Discount reason" disabled={!editable} value={editable ? draft.discount.reason : value?.discount?.reason || ""} onChange={(event) => onChange({ ...draft, discount: { ...draft.discount, reason: event.target.value } })} />
        </label>
      </div>
      <dl className="ml-auto grid max-w-lg grid-cols-[1fr_auto] gap-x-8 gap-y-2 rounded-sm border p-4 text-sm" data-testid="service-cost-summary">
        <dt className="text-muted-foreground">Gross Service Cost</dt><dd className="text-right tabular-nums">{formatServiceCost(summary.gross)}</dd>
        <dt className="text-muted-foreground">Discount Amount</dt><dd className="text-right tabular-nums">{formatServiceCost(summary.discountAmount)}</dd>
        <dt className="font-semibold">Final Service Cost</dt><dd className="text-right font-semibold tabular-nums text-primary">{formatServiceCost(summary.final)}</dd>
      </dl>
      {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
    </div>
  );
}

export default function ServiceCostSection({ serviceTicketScope3 = false, ...props }) {
  return serviceTicketScope3
    ? <UnifiedServiceCostSection {...props} />
    : <LegacyServiceCostSection {...props} />;
}
